import { useRef, useCallback } from "react";

interface UseCarouselDragOptions {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  threshold?: number;
}

export function useCarouselDrag({
  onSwipeLeft,
  onSwipeRight,
  threshold = 50,
}: UseCarouselDragOptions) {
  const startX = useRef(0);
  const isDragging = useRef(false);

  const handleDragStart = useCallback((clientX: number) => {
    startX.current = clientX;
    isDragging.current = true;
  }, []);

  const handleDragEnd = useCallback(
    (clientX: number) => {
      if (!isDragging.current) return;
      isDragging.current = false;

      const diff = startX.current - clientX;
      if (Math.abs(diff) >= threshold) {
        if (diff > 0) {
          onSwipeLeft();
        } else {
          onSwipeRight();
        }
      }
    },
    [onSwipeLeft, onSwipeRight, threshold]
  );

  const dragProps = {
    onTouchStart: (e: React.TouchEvent) => {
      handleDragStart(e.touches[0].clientX);
    },
    onTouchEnd: (e: React.TouchEvent) => {
      handleDragEnd(e.changedTouches[0].clientX);
    },
    onMouseDown: (e: React.MouseEvent) => {
      handleDragStart(e.clientX);
    },
    onMouseUp: (e: React.MouseEvent) => {
      handleDragEnd(e.clientX);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      if (isDragging.current) {
        handleDragEnd(e.clientX);
      }
    },
    style: { cursor: "grab" } as React.CSSProperties,
  };

  return dragProps;
}
